import { FileSpreadsheet, FileUp, Layers3, LoaderCircle, Route, UploadCloud } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { createResource, extractErrorMessage, uploadFormResource } from "../../lib/api";

const SummaryCard = ({ label, testId, value }) => (
  <div className="rounded-3xl border border-zinc-200 bg-zinc-50 px-5 py-4" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</p>
    <p className="mt-2 text-3xl font-semibold text-zinc-900">{value}</p>
  </div>
);

const IMPORT_MODES = {
  simple: {
    value: "simple",
    label: "Simple Import",
    icon: Route,
    title: "Import one completed load per row",
    description: "Best for straightforward completed history. Each row becomes one completed load with one shipment ID.",
    uploadTitle: "Upload simple completed-load CSV",
    uploadHint: "Use one row per completed load. This mode is for basic history without repeated load numbers.",
    templateHref: "/completed-load-import-simple-template.csv",
    templateLabel: "Download simple template",
    previewEndpoint: "/loads/import/completed/simple/preview",
    validateEndpoint: "/loads/import/completed/simple/validate",
    commitEndpoint: "/loads/import/completed/simple/commit",
    successLabel: "simple completed loads",
    mappingHint: "Map one-row-per-load history fields before validating the import.",
    optionsHint: "Existing completed loads are matched by Load Number + Customer Ref #.",
    totalRowsLabel: "Load rows",
    totalLoadsLabel: "Loads",
    readyLoadsLabel: "Ready loads",
    skippedLoadsLabel: "Skipped loads",
    failedLoadsLabel: "Failed loads",
    validatedHint: "Showing the first validated simple completed rows.",
    importButtonLabel: "Import simple completed loads",
    importedRowsLabel: "Imported loads",
  },
  multi_leg: {
    value: "multi_leg",
    label: "Multi-Leg Import",
    icon: Layers3,
    title: "Import repeated load numbers with chained legs",
    description: "Best for drop-and-hook history. Repeated load numbers stay together, each shipment ID stays under that same load, and legs create separate trip records in sequence.",
    uploadTitle: "Upload completed multi-leg CSV",
    uploadHint: "Use one row per leg. Repeated load numbers are grouped into one completed load, and each next leg must continue from the prior delivery.",
    templateHref: "/completed-load-import-template.csv",
    templateLabel: "Download multi-leg template",
    previewEndpoint: "/loads/import/completed/preview",
    validateEndpoint: "/loads/import/completed/validate",
    commitEndpoint: "/loads/import/completed/commit",
    successLabel: "completed leg rows",
    mappingHint: "Map leg-level CSV fields before validating the grouped shipment import.",
    optionsHint: "Repeated load numbers are grouped into one completed load and matched against existing data by Load Number + Customer Ref #.",
    totalRowsLabel: "Leg rows",
    totalLoadsLabel: "Load groups",
    readyLoadsLabel: "Ready loads",
    skippedLoadsLabel: "Skipped loads",
    failedLoadsLabel: "Failed loads",
    validatedHint: "Showing the first validated multi-leg rows.",
    importButtonLabel: "Import completed loads",
    importedRowsLabel: "Imported legs",
  },
};

export const CompletedLoadImportPanel = () => {
  const [importMode, setImportMode] = useState("multi_leg");
  const [previewSession, setPreviewSession] = useState(null);
  const [mapping, setMapping] = useState({});
  const [duplicateStrategy, setDuplicateStrategy] = useState("skip");
  const [validation, setValidation] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState({ upload: false, validate: false, import: false });

  const modeConfig = IMPORT_MODES[importMode];
  const previewRows = validation?.rows || [];
  const previewTableRows = useMemo(() => previewRows.slice(0, 30), [previewRows]);

  const handleModeChange = (nextMode) => {
    setImportMode(nextMode);
    setPreviewSession(null);
    setMapping({});
    setValidation(null);
    setImportResult(null);
    setDuplicateStrategy("skip");
  };

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      setBusy((current) => ({ ...current, upload: true }));
      const formData = new FormData();
      formData.append("file", file);
      const payload = await uploadFormResource(modeConfig.previewEndpoint, formData);
      setPreviewSession(payload);
      setMapping(payload.suggested_mapping || {});
      setValidation(null);
      setImportResult(null);
      toast.success(`Loaded ${payload.total_rows} ${modeConfig.successLabel}.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to read that ${modeConfig.label.toLowerCase()} CSV.`));
    } finally {
      setBusy((current) => ({ ...current, upload: false }));
    }
  };

  const validateImport = async () => {
    if (!previewSession) return;
    try {
      setBusy((current) => ({ ...current, validate: true }));
      const payload = await createResource(modeConfig.validateEndpoint, {
        import_log_id: previewSession.import_log_id,
        field_mapping: mapping,
        duplicate_strategy: duplicateStrategy,
      });
      setValidation(payload);
      setImportResult(null);
      toast.success(`${modeConfig.label} preview validated.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to validate the ${modeConfig.label.toLowerCase()} rows.`));
    } finally {
      setBusy((current) => ({ ...current, validate: false }));
    }
  };

  const commitImport = async () => {
    if (!previewSession) return;
    try {
      setBusy((current) => ({ ...current, import: true }));
      const payload = await createResource(modeConfig.commitEndpoint, {
        import_log_id: previewSession.import_log_id,
        field_mapping: mapping,
        duplicate_strategy: duplicateStrategy,
      });
      setImportResult(payload);
      toast.success(`Imported ${payload.summary.imported_loads} ${modeConfig.successLabel === "completed leg rows" ? "completed loads" : "simple completed loads"}.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, `Unable to complete the ${modeConfig.label.toLowerCase()}.`));
    } finally {
      setBusy((current) => ({ ...current, import: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="completed-load-import-panel">
      <section className="rounded-[32px] border border-zinc-200 bg-[linear-gradient(135deg,#f8fafc_0%,#ffffff_55%,#ecfeff_100%)] px-6 py-6">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Completed Loads / Multi-Leg Import</p>
        <h2 className="mt-2 text-3xl font-semibold text-zinc-900">Choose your completed import mode</h2>
        <div className="mt-6 grid gap-3 md:grid-cols-2" data-testid="completed-load-import-mode-toggle">
          {Object.values(IMPORT_MODES).map((mode) => {
            const Icon = mode.icon;
            const active = importMode === mode.value;
            return (
              <button
                className={`rounded-[28px] border px-5 py-5 text-left transition-colors ${active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-900 hover:border-zinc-400"}`}
                data-testid={`completed-load-import-mode-${mode.value}`}
                key={mode.value}
                onClick={() => handleModeChange(mode.value)}
                type="button"
              >
                <div className="flex items-start gap-3">
                  <div className={`rounded-2xl p-3 ${active ? "bg-white/10 text-white" : "bg-zinc-100 text-zinc-700"}`}><Icon className="h-5 w-5" /></div>
                  <div>
                    <div className="text-base font-semibold">{mode.label}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-[32px] border-2 border-dashed border-zinc-300 bg-white px-6 py-10 text-center" data-testid="completed-load-import-dropzone">
        <UploadCloud className="mx-auto h-10 w-10 text-zinc-400" />
        <h3 className="mt-4 text-2xl font-semibold text-zinc-900">{modeConfig.uploadTitle}</h3>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="completed-load-import-upload-button">
            {busy.upload ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
            Choose CSV file
            <input accept=".csv" className="hidden" onChange={(event) => handleUpload(event.target.files?.[0])} type="file" />
          </label>
          <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="completed-load-import-template-link" href={modeConfig.templateHref}>
            <FileSpreadsheet className="h-4 w-4" /> {modeConfig.templateLabel}
          </a>
        </div>
      </section>

      {previewSession ? (
        <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="table-shell">
            <div className="border-b border-zinc-200 px-5 py-4">
              <h2 className="text-2xl font-semibold text-zinc-900">Field mapping</h2>
            </div>
            <div className="overflow-x-auto px-5 py-5">
              <table className="data-table" data-testid="completed-load-import-mapping-table">
                <thead>
                  <tr>
                    <th>TMS field</th>
                    <th>CSV column</th>
                  </tr>
                </thead>
                <tbody>
                  {previewSession.field_options.map((field) => (
                    <tr key={field.key}>
                      <td>
                        <p className="font-medium text-zinc-900">{field.label}</p>
                        <p className="mt-1 text-xs text-zinc-500">{field.required ? "Required" : "Optional"}</p>
                      </td>
                      <td>
                        <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid={`completed-load-import-mapping-${field.key}`} onChange={(event) => setMapping((current) => ({ ...current, [field.key]: event.target.value || null }))} value={mapping[field.key] || ""}>
                          <option value="">Ignore for now</option>
                          {previewSession.headers.map((header) => <option key={`${field.key}-${header}`} value={header}>{header}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-6">
            <div className="panel-surface px-5 py-5" data-testid="completed-load-import-options-card">
              <h2 className="text-2xl font-semibold text-zinc-900">Import options</h2>
              <div className="mt-4 space-y-3">
                {[
                  { value: "skip", label: "Skip existing completed loads" },
                  { value: "update", label: "Update existing completed loads" },
                  { value: "duplicate", label: "Create duplicate copies as new load numbers" },
                ].map((option) => (
                  <label className="flex items-start gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-4" key={option.value}>
                    <input checked={duplicateStrategy === option.value} data-testid={`completed-load-import-duplicate-${option.value}`} name="completedDuplicateStrategy" onChange={() => setDuplicateStrategy(option.value)} type="radio" />
                    <span className="text-sm text-zinc-700">{option.label}</span>
                  </label>
                ))}
              </div>
              <button className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white" data-testid="completed-load-import-validate-button" onClick={validateImport} type="button">
                {busy.validate ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                Validate preview
              </button>
            </div>

            <div className="panel-surface px-5 py-5" data-testid="completed-load-import-raw-preview-card">
              <h2 className="text-2xl font-semibold text-zinc-900">CSV preview</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="data-table text-xs" data-testid="completed-load-import-raw-preview-table">
                  <thead>
                    <tr>{previewSession.headers.map((header) => <th key={header}>{header}</th>)}</tr>
                  </thead>
                  <tbody>
                    {previewSession.sample_rows.map((row, index) => (
                      <tr key={`completed-sample-${index}`}>
                        {previewSession.headers.map((header) => <td key={`${index}-${header}`}>{row[header] || "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {validation ? (
        <section className="space-y-6" data-testid="completed-load-import-validation-section">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label={modeConfig.totalRowsLabel} testId="completed-load-import-total-rows" value={validation.summary.total_rows} />
            <SummaryCard label={modeConfig.totalLoadsLabel} testId="completed-load-import-total-loads" value={validation.summary.total_loads} />
            <SummaryCard label={modeConfig.readyLoadsLabel} testId="completed-load-import-ready-loads" value={validation.summary.importable_loads} />
            <SummaryCard label={modeConfig.skippedLoadsLabel} testId="completed-load-import-skipped-loads" value={validation.summary.skipped_loads} />
            <SummaryCard label={modeConfig.failedLoadsLabel} testId="completed-load-import-failed-loads" value={validation.summary.failed_loads} />
          </div>

          <div className="table-shell">
            <div className="flex flex-col gap-3 border-b border-zinc-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-zinc-900">Validated rows</h2>
              </div>
              <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-zinc-400" data-testid="completed-load-import-commit-button" disabled={!validation.summary.importable_loads || busy.import} onClick={commitImport} type="button">
                {busy.import ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                {modeConfig.importButtonLabel}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="completed-load-import-validation-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Load</th>
                    <th>Shipment ID</th>
                    <th>Customer Ref</th>
                    <th>Leg</th>
                    <th>Route</th>
                    <th>Assignment</th>
                    <th>Action</th>
                    <th>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {previewTableRows.map((row) => (
                    <tr key={`completed-validated-${row.row_number}`}>
                      <td>{row.row_number}</td>
                      <td>{row.load_number || "—"}</td>
                      <td>{row.shipment_id || "Auto"}</td>
                      <td>{row.customer_reference_number || "—"}</td>
                      <td>{row.leg_number || (importMode === "simple" ? "1" : "—")}</td>
                      <td>{row.route || "—"}</td>
                      <td>{row.assignment || "—"}</td>
                      <td>{row.group_action || "—"}</td>
                      <td>{row.errors.length ? row.errors.map((error) => <p className="text-xs text-red-600" key={`${row.row_number}-${error}`}>{error}</p>) : <span className="text-xs font-semibold text-emerald-600">Ready</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {importResult ? (
        <section className="panel-surface px-5 py-5" data-testid="completed-load-import-result-card">
          <h2 className="text-2xl font-semibold text-zinc-900">Import summary</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <SummaryCard label="Total rows" testId="completed-load-import-result-total-rows" value={importResult.summary.total_rows} />
            <SummaryCard label="Total loads" testId="completed-load-import-result-total-loads" value={importResult.summary.total_loads} />
            <SummaryCard label="Imported loads" testId="completed-load-import-result-imported-loads" value={importResult.summary.imported_loads} />
            <SummaryCard label={modeConfig.importedRowsLabel} testId="completed-load-import-result-imported-rows" value={importResult.summary.imported_rows} />
            <SummaryCard label="Failed loads" testId="completed-load-import-result-failed-loads" value={importResult.summary.failed_loads} />
          </div>
        </section>
      ) : null}
    </div>
  );
};